/**
 * Session-path helpers for the debug panel.
 *
 * Mirrors the host-side helpers in `host/src/chain.ts` (PDA derivation,
 * randomSessionId, normaliseStateHash) and adds a 1Hz polling hook for the
 * Session PDA modeled on `client/src/games/last-touch/useKothRound.ts`.
 *
 * Lives under `client/src/dev/` because the only consumer is the debug panel's
 * ChainTab — the Session ix path isn't surfaced in any user-facing game yet.
 */
import { BN } from "@coral-xyz/anchor";
import { useConnection } from "@solana/wallet-adapter-react";
import { PublicKey } from "@solana/web3.js";
import { Buffer } from "buffer";
import { useCallback, useEffect, useMemo, useState } from "react";

import { buildReadonlyProgram } from "../explorer/anchor";

/** localStorage key shared by URL ?session=<hex> and reload-cache fallback. */
export const SESSION_LS_KEY = "last_touch.activeSessionIdHex";

const SESSION_SEED = Buffer.from("session");
const HOST_SEED = Buffer.from("host");
const TREASURY_SEED = Buffer.from("treasury");
const SESSION_ID_BYTES = 32;

export function deriveSessionPda(
  sessionId: Uint8Array,
  programId: PublicKey,
): PublicKey {
  if (sessionId.length !== SESSION_ID_BYTES) {
    throw new Error(
      `deriveSessionPda: session_id must be ${SESSION_ID_BYTES} bytes (got ${sessionId.length})`,
    );
  }
  const [pda] = PublicKey.findProgramAddressSync(
    [SESSION_SEED, Buffer.from(sessionId)],
    programId,
  );
  return pda;
}

export function deriveHostPda(
  authority: PublicKey,
  programId: PublicKey,
): PublicKey {
  const [pda] = PublicKey.findProgramAddressSync(
    [HOST_SEED, authority.toBuffer()],
    programId,
  );
  return pda;
}

export function deriveTreasuryPda(programId: PublicKey): PublicKey {
  const [pda] = PublicKey.findProgramAddressSync([TREASURY_SEED], programId);
  return pda;
}

/** Generate a 32-byte cryptographically random session id. */
export function randomSessionId(): Uint8Array {
  const buf = new Uint8Array(SESSION_ID_BYTES);
  crypto.getRandomValues(buf);
  return buf;
}

/** Format 32 bytes as a lowercase hex string (no `0x` prefix). */
export function sessionIdToHex(bytes: Uint8Array): string {
  let out = "";
  for (let i = 0; i < bytes.length; i++) {
    out += bytes[i]!.toString(16).padStart(2, "0");
  }
  return out;
}

/** Parse a 64-char hex string (with or without `0x`) into 32 bytes. */
export function hexToSessionId(hex: string): Uint8Array {
  const stripped = hex.startsWith("0x") ? hex.slice(2) : hex;
  if (stripped.length !== 64) {
    throw new Error(
      `expected 32-byte hex (got ${stripped.length} chars)`,
    );
  }
  const out = new Uint8Array(SESSION_ID_BYTES);
  for (let i = 0; i < SESSION_ID_BYTES; i++) {
    const byte = Number.parseInt(stripped.slice(i * 2, i * 2 + 2), 16);
    if (!Number.isFinite(byte)) {
      throw new Error(`invalid hex byte at offset ${i}`);
    }
    out[i] = byte;
  }
  return out;
}

/** SHA-256 a UTF-8 string and return the 32-byte digest. */
export async function sha256Hex(input: string): Promise<string> {
  const enc = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest("SHA-256", enc);
  return sessionIdToHex(new Uint8Array(digest));
}

/**
 * Decoded shape of the on-chain `Session` account. Mirrors `state.rs::Session`.
 *
 * `host` and downstream fields are marked optional because the IDL/runtime
 * decoder will silently return `undefined` for fields not present on
 * older-binary PDAs — see [[anchor-account-schema-migration-old-pda-silent-partial-decode]].
 * The current program has only ever shipped one Session layout so in practice
 * all fields are populated, but the optional shape keeps callers honest.
 */
export interface SessionState {
  sessionId: number[] | Uint8Array;
  host: PublicKey;
  escrowLamports: BN;
  /** 0=Created, 1=Live, 2=ResultSubmitted, 3=Paid. */
  status: number;
  players: PublicKey[];
  stateHash: number[] | Uint8Array;
  winner: PublicKey;
  bump: number;
}

export interface UseSessionResult {
  session: SessionState | null;
  pda: PublicKey | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

const POLL_MS = 1000;

/**
 * 1Hz poller for a Session PDA. Returns `session = null` when the PDA does
 * not exist (caller renders an init CTA). Stops polling once `status === 3`
 * (Paid) — settled sessions don't change again on-chain.
 */
export function useSession(sessionId: Uint8Array | null): UseSessionResult {
  const { connection } = useConnection();
  const [session, setSession] = useState<SessionState | null>(null);
  const [loading, setLoading] = useState<boolean>(sessionId !== null);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const program = useMemo(() => buildReadonlyProgram(connection), [connection]);
  const pda = useMemo(
    () => (sessionId ? deriveSessionPda(sessionId, program.programId) : null),
    [sessionId, program.programId],
  );

  const refresh = useCallback(() => setRefreshTick((n) => n + 1), []);

  useEffect(() => {
    if (!pda) {
      setSession(null);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const ns = (
      program.account as unknown as Record<
        string,
        { fetch: (pk: PublicKey) => Promise<unknown> }
      >
    )["session"];
    if (!ns || typeof ns.fetch !== "function") {
      setError("IDL is missing the Session account schema");
      setLoading(false);
      return;
    }

    const tick = async (): Promise<void> => {
      try {
        const acct = (await ns.fetch(pda)) as SessionState;
        if (cancelled) return;
        setSession(acct);
        setError(null);
        setLoading(false);
        if (acct.status === 3) return; // Paid — terminal
      } catch (e) {
        if (cancelled) return;
        const msg = (e as Error).message ?? String(e);
        if (/does not exist|Account does not exist/i.test(msg)) {
          setSession(null);
          setError(null);
        } else {
          setError(msg);
        }
        setLoading(false);
      }
      if (!cancelled) timer = setTimeout(() => void tick(), POLL_MS);
    };
    void tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [program, pda, refreshTick]);

  return { session, pda, loading, error, refresh };
}
